import React, { useEffect, useMemo, useRef, useState } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Progress } from "../components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { askDocs, completeUpload, initUpload, listDocs, uploadChunk } from "../services/knowledge";

const CHUNK_SIZE = 512 * 1024; // 512KB

export default function KnowledgeBank() {
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [ans, setAns] = useState("");
  const [progress, setProgress] = useState(0);

  const refresh = async () => {
    const d = await listDocs();
    setDocs(d);
  };

  useEffect(() => { refresh(); }, []);

  const onFile = async (file) => {
    setBusy(true); setProgress(0);
    try {
      const { upload_id } = await initUpload({ filename: file.name, size: file.size, mime: file.type });
      const total = Math.ceil(file.size / CHUNK_SIZE);
      for (let i = 0; i < total; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(file.size, start + CHUNK_SIZE);
        const blob = file.slice(start, end);
        await uploadChunk({ uploadId: upload_id, index: i, total, blob });
        setProgress(Math.round(((i + 1) / total) * 100));
      }
      await completeUpload({ uploadId: upload_id });
      await refresh();
    } catch (e) {
      alert(`Upload failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const ask = async () => {
    setBusy(true); setAns("");
    try {
      const { answer, used_docs } = await askDocs(q);
      setAns(`${answer}\n\nUsed: ${used_docs.join(", ")}`);
    } catch(e){ setAns(`Error: ${e.message}`); }
    setBusy(false);
  };

  return (
    <div className="space-y-6">
      <Card className="bg-white border-gray-200 text-[#383838]"><CardContent className="p-4">
        <div className="flex items-center gap-2">
          <Input type="file" onChange={(e)=>e.target.files?.[0] && onFile(e.target.files[0])} disabled={busy} className="bg-gray-50 border-gray-200" />
          <div className="w-48"><Progress value={progress} /></div>
          <span className="text-xs text-[#616161]">{busy?`Uploading… ${progress}%`:"Upload PDF/TXT/MD"}</span>
        </div>
      </CardContent></Card>

      <Card className="bg-white border-gray-200 text-[#383838]"><CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Input placeholder="Ask the knowledge bank…" value={q} onChange={(e)=>setQ(e.target.value)} className="bg-gray-50 border-gray-200" />
          <Button className="bg-cyan-700 hover:bg-cyan-600" onClick={ask} disabled={!q || busy}>{busy?"Thinking…":"Ask"}</Button>
        </div>
        <div className="text-sm whitespace-pre-wrap bg-gray-50 rounded p-3 min-h-24">{ans||"Answer will appear here."}</div>
      </CardContent></Card>

      <Card className="bg-white border-gray-200 text-[#383838]"><CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Document</TableHead>
              <TableHead>Size</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Added</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {docs.map(d => (
              <TableRow key={d.id} className="hover:bg-white">
                <TableCell className="font-medium">{d.filename}</TableCell>
                <TableCell>{Math.round((d.size||0)/1024)} KB</TableCell>
                <TableCell>{d.mime||"-"}</TableCell>
                <TableCell>{new Date(d.createdAt).toLocaleString()}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
}